import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { Formik, Form, Field, FieldArray, getIn, setIn } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Yup from "yup";
import {
  faSpinner,
  faAngleRight,
  faAngleLeft,
  faEllipsis,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { getCustomerForUI } from "../../store/reducer/customers/customersSlice";
import { getOpportunityByCustomerId, getOpportunityIdsForFinancials } from "../../store/reducer/opportunity/opportunitySlice";
import { getCustomerSitesByCustomerForUI } from "../../store/reducer/customers/customerSiteSlice";
import { getInvoicesForFinancials } from "../../store/reducer/invoices/invoiceSlice";
import { getExpensesByOpportunity } from "../../store/reducer/expenses/expensesSlice";
import { getBusinessMileageByOpportunity } from "../../store/reducer/businessMileage/businessMileageSlice";
import { getOpportunityBudgetByOpportunityId } from "../../store/reducer/opportunityBudget/opportunityBudgetSlice";
import { getBusinessTimeEntryByOpportunity } from "../../store/reducer/businessTimeEntry/businessTimeEntrySlice";

const toCamelCase = (str) => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
    .replace(/^[A-Z]/, (match) => match.toLowerCase());
};

// Normalize keys in an object
const normalizeKeys = (obj) => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(normalizeKeys);
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    const camelCaseKey = toCamelCase(key);
    acc[camelCaseKey] = normalizeKeys(value);
    return acc;
  }, {});
};

function formatDateToDDMMYYYY(dateString) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}
function formatCurrency(amount) {
  const n = Number(amount) || 0;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(n);
}

function Financials() {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [customerData, setCustomerData] = useState([]);
  const [siteData, setSiteData] = useState([]);
  const [opportunityData, setOpportunityData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [bills, setBills] = useState([]);
  const [projectExpenseData, setProjectExpenseData] = useState([]);
  const [projectMileageData, setProjectMileageData] = useState([]);
  const [projectTimeEntryData, setProjectTimeEntryData] = useState([]);
  const [profitLoss, setProfitLoss] = useState({ budget: 0, actual: 0 });
  const [profitLossPercentage, setProfitLossPercentage] = useState({ budget: 0, actual: 0 });
  const [cost, setCost] = useState({
    budget: 0,
    actual: 0,
    internalCost: 0,       // <-- ensure keys exist initially
    salesCommission: 0
  });
  const [revenue, setRevenue] = useState({ budget: 0, actual: 0 });
  const [oppSelectOptions, setOppSelectOptions] = useState([]);

  // NEW: toggles that affect Budget cost
  const [includeCommission, setIncludeCommission] = useState(false);
  const [includeInternalCosts, setIncludeInternalCosts] = useState(false);
  const [selectedDrawingId, setSelectedDrawingId] = useState("");

  const { loading } = useSelector((state) => state.invoices);

  const validationSchema = Yup.object().shape({
    opportunity_id: Yup.number().required('Opportunity id is required'),
  });

  useEffect(() => {
    dispatch(getCustomerForUI()).then(result => {
      if (result.payload) setCustomerData(result.payload);
    });
    dispatch(getOpportunityIdsForFinancials()).then((result) => {
      if (result.payload) setOppSelectOptions(result.payload);
    });
  }, []);

  const getSiteByCustomerId = (customer_id) => {
    dispatch(getCustomerSitesByCustomerForUI({ customer_id })).then(
      (result) => {
        if (result.payload) setSiteData(result.payload);
      }
    );
  };

  const getOpportunityBySiteId = (site_id, customer_id) => {
    dispatch(getOpportunityByCustomerId({ customer_id })).then(
      (result) => {
        if (result.payload) {
          const opportunity = result.payload?.filter(opp => opp.site_id == site_id);
          setOpportunityData(opportunity);
        }
      }
    );
  };

  const ErrorMessage = ({ name }) => (
    <Field
      name={name}
      render={({ form }) => {
        const error = getIn(form.errors, name);
        const touch = getIn(form.touched, name);
        return touch && error ? (
          <div className="text-red-700">{error}</div>
        ) : null;
      }}
    />
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    // reset UI
    setInvoices([]);
    setBills([]);
    setProjectExpenseData([]);
    setProjectMileageData([]);
    setProjectTimeEntryData([]);
    setOpportunityData(null);
    setCost({ budget: 0, actual: 0, internalCost: 0, salesCommission: 0 });
    setRevenue({ budget: 0, actual: 0 });
    setProfitLoss({ budget: 0, actual: 0 });
    setProfitLossPercentage({ budget: 0, actual: 0 });

    try {
      const [
        invoicesResult,
        expensesResult,
        mileageResult,
        budgetResult,
        timeEntryResult,
      ] = await Promise.all([
        dispatch(getInvoicesForFinancials(values.opportunity_id)),
        dispatch(getExpensesByOpportunity(values.opportunity_id)),
        dispatch(getBusinessMileageByOpportunity(values.opportunity_id)),
        dispatch(getOpportunityBudgetByOpportunityId(values.drawing_id)),
        dispatch(getBusinessTimeEntryByOpportunity(values.opportunity_id)),
      ]);

      let totalRevenue = 0;
      let totalCost = 0;

      if (invoicesResult.payload) {
        setInvoices(invoicesResult.payload.invoices);
        setBills(invoicesResult.payload.bills);
        setOpportunityData(invoicesResult.payload.opportunity);

        totalRevenue += +invoicesResult.payload.totalInvoices;
        totalCost += +invoicesResult.payload.totalBills;

        totalRevenue -= +invoicesResult.payload.totalCreditNoteInvoices;
        totalCost -= +invoicesResult.payload.totalCreditNoteBills;
      }

      if (expensesResult.payload) {
        setProjectExpenseData(expensesResult.payload);
        totalCost += expensesResult.payload.reduce(
          (sum, e) => sum + (+e.line_amount || 0),
          0
        );
      }

      if (mileageResult.payload) {
        setProjectMileageData(mileageResult.payload);
        totalCost += mileageResult.payload.reduce(
          (sum, m) => sum + (+m.cost || 0),
          0
        );
      }

      if (timeEntryResult.payload) {
        setProjectTimeEntryData(timeEntryResult.payload);
        totalCost += timeEntryResult.payload.reduce(
          (sum, t) => sum + (+t.cost || 0),
          0
        );
      }

      if (budgetResult.payload) {
        setCost((p) => ({
          ...p,
          budget: +budgetResult.payload["Total Cost"],
          internalCost: +budgetResult.payload["Internal Costs"],
          salesCommission: +budgetResult.payload["Sales Commission"],
        }));
        setRevenue((p) => ({ ...p, budget: +budgetResult.payload["Total Sell"] }));

        const budgetPL = (+budgetResult.payload["Total Sell"]) - (+budgetResult.payload["Total Cost"]);
        setProfitLoss((p) => ({ ...p, budget: Number(budgetPL.toFixed(2)) }));
        setProfitLossPercentage((p) => ({
          ...p,
          budget: Number(
            (+budgetResult.payload["Total Sell"]
              ? (budgetPL / +budgetResult.payload["Total Sell"]) * 100
              : 0
            ).toFixed(2)
          ),
        }));
      }

      const actualPL = totalRevenue - totalCost;
      const actualPLPct = totalRevenue ? (actualPL / totalRevenue) * 100 : 0;

      setCost((p) => ({ ...p, actual: Number(totalCost.toFixed(2)) }));
      setRevenue((p) => ({ ...p, actual: Number(totalRevenue.toFixed(2)) }));
      setProfitLoss((p) => ({ ...p, actual: Number(actualPL.toFixed(2)) }));
      setProfitLossPercentage((p) => ({ ...p, actual: Number(actualPLPct.toFixed(2)) }));

      toast.success("Financial data loaded");
    } catch (err) {
      setInvoices([]);
      setBills([]);
      setProjectExpenseData([]);
      setProjectMileageData([]);
      setProjectTimeEntryData([]);
      setOpportunityData(null);
      setCost({ budget: 0, actual: 0, internalCost: 0, salesCommission: 0 });
      setRevenue({ budget: 0, actual: 0 });
      setProfitLoss({ budget: 0, actual: 0 });
      setProfitLossPercentage({ budget: 0, actual: 0 });

      toast.error("An error occurred while fetching data");
    } finally {
      setSubmitting(false);
    }
  };

  // ------- Derived values affected by the checkboxes -------
  const computedBudgetCost =
    (Number(cost?.budget) || 0) +
    (includeCommission ? (Number(cost?.salesCommission) || 0) : 0) +
    (includeInternalCosts ? (Number(cost?.internalCost) || 0) : 0);

  const costVariance = (Number(cost?.actual) || 0) - computedBudgetCost;

  const computedBudgetPL = (Number(revenue?.budget) || 0) - computedBudgetCost;
  const computedBudgetPLPct = (Number(revenue?.budget) || 0)
    ? (computedBudgetPL / Number(revenue?.budget)) * 100
    : 0;

  return (
    <>
      <div className="flex flex-col mt-[20px] gap-[40px]">
        <div className="bg-grey md:flex gap-[10px]">
          <div className="w-full md:flex gap-[24px] flex-col bg-[#F8F8F8] rounded-[12px] p-6">
            <div className="flex flex-row justify-between pb-[20px] border-b border-[#E1E1E1] border-solid">
              <div>
                <span className="font-Inter font-[600] text-[22px] leading-[21px] text-[#1C1C20]">
                  Financials
                </span>
              </div>
            </div>
            <Formik
              innerRef={formRef}
              initialValues={{ customer_id: '', site_id: '', opportunity_id: '', drawing_id: '' }}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {({
                isSubmitting,
                values,
                errors,
                touched,
                setFieldValue,
                resetForm,
                handleSubmit,
              }) => (
                <Form>
                  <div className="md:grid md:grid-cols-4 gap-3">
                    <Field
                      as="select"
                      id="opportunity_id"
                      name="opportunity_id"
                      className="block w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      onChange={(e) => {
                        const val = e.target.value;
                        setFieldValue("opportunity_id", val);
                        const selected = oppSelectOptions.find(
                          (o) => String(o.opportunity_id) === String(val)
                        );
                        setFieldValue("drawing_id", selected?.drawing_id ?? "");
                        setSelectedDrawingId(selected?.drawing_id ?? "");
                      }}
                    >
                      <option value="">Select Opportunity</option>
                      {oppSelectOptions.map((o) => (
                        <option key={o.opportunity_id} value={o.opportunity_id}>
                          {`${o.opportunity_id} - ${o.drawing_id} - ${o.customer_name} - ${o.site_name}`}
                        </option>
                      ))}
                    </Field>

                    <div className="w-full bg-[#F8F8F8] rounded-[12px]">
                      <div>
                        <button
                          type="submit"
                          name="submit"
                          className={`py-[10px] px-[70px] text-center text-[#55A14A] border border-[#55A14A] w-full rounded-[12px] text-[18px] bg-white`}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            "Calculate"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      <div className="flex flex-col mt-[40px] gap-[20px]">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col">
            <span className="text-xl font-semibold text-gray-800 mb-4">
              Financial Summary
            </span>
            {opportunityData &&
              <span className="text-l text-gray-800 mb-4">
                <b>Customer:</b> {opportunityData.customer_name || 'NA'}
                <br />
                <b>Site:</b> {opportunityData.site_name || 'NA'}
                <br />
                <b>Opportunity:</b>{" "}
                {(opportunityData?.id ?? 'NA') + " : " +
                (opportunityData?.drawing_id ?? selectedDrawingId ?? 'NA') + " : " +
                (opportunityData?.opportunity_name ?? 'NA')}
                <br />
                <b>Sales Rep:</b> {(opportunityData.sales_exec_firstname + ' ' + opportunityData.sales_exec_lastname) || 'NA'}
              </span>
            }

            {/* Revenue and Costs Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Revenue Section */}
            <div className="p-4 rounded-lg shadow-md">
              {/* spacer to match the toggles row height in Costs */}
              <div className="mb-3 min-h-[40px]" />

              <div className="text-lg font-medium text-gray-600">Revenue</div>
              <div className="grid grid-cols-3 mt-2 gap-x-4">
                <div className="text-sm font-medium text-gray-600">Budget</div>
                <div className="text-sm font-medium text-gray-600">Actual</div>
                <div className="text-sm font-medium text-gray-600">Variance</div>
              </div>
              <div className="grid grid-cols-3 mt-2 gap-x-4">
                <div className="text-xl font-bold text-gray-800">{formatCurrency(revenue?.budget)}</div>
                <div className="text-xl font-bold text-gray-800">{formatCurrency(revenue?.actual)}</div>
                <div className="text-xl font-bold text-gray-800">
                  {formatCurrency((Number(revenue?.actual) || 0) - (Number(revenue?.budget) || 0))}
                </div>
              </div>
            </div>

            {/* Costs Section */}
            <div className="p-4 rounded-lg shadow-md">
              {/* Include toggles (same height as spacer above) */}
              <div className="mb-3 min-h-[40px] flex flex-wrap items-center gap-6">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={includeCommission}
                    onChange={(e) => setIncludeCommission(e.target.checked)}
                  />
                  Include Commission
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={includeInternalCosts}
                    onChange={(e) => setIncludeInternalCosts(e.target.checked)}
                  />
                  Include Internal Costs
                </label>
              </div>

              <div className="text-lg font-medium text-gray-600">Costs</div>
              <div className="grid grid-cols-3 mt-2 gap-x-4">
                <div className="text-sm font-medium text-gray-600">Budget</div>
                <div className="text-sm font-medium text-gray-600">Actual</div>
                <div className="text-sm font-medium text-gray-600">Variance</div>
              </div>
              <div className="grid grid-cols-3 mt-2 gap-x-4">
                <div className="text-xl font-bold text-gray-800">{formatCurrency(computedBudgetCost)}</div>
                <div className="text-xl font-bold text-gray-800">{formatCurrency(cost?.actual)}</div>
                <div className="text-xl font-bold text-gray-800">{formatCurrency(costVariance)}</div>
              </div>

              {/* Below Budget: show components */}
              <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                <div className="text-gray-600">Internal Costs</div>
                <div className="text-gray-800 font-medium">{formatCurrency(cost?.internalCost)}</div>
                <div className="text-gray-600">Sales Commission</div>
                <div className="text-gray-800 font-medium">{formatCurrency(cost?.salesCommission)}</div>
              </div>
            </div>
          </div>

            {/* Profit/Loss Row */}
            <div className="grid grid-cols-1 gap-6 mt-6">
              <div className="p-4 rounded-lg shadow-md">
                <div className="text-lg font-medium text-gray-600">Profit / Loss</div>
                <div className="grid grid-cols-3 mt-2 gap-x-4 text-center">
                  <div className="text-sm font-medium text-gray-600">Budget</div>
                  <div className="text-sm font-medium text-gray-600">Actual</div>
                  <div className="text-sm font-medium text-gray-600">Variance</div>
                </div>
                <div className="grid grid-cols-3 mt-2 gap-x-4 text-center">
                  <div className="text-xl font-bold text-gray-800">
                    {formatCurrency(computedBudgetPL)} ({(computedBudgetPLPct).toFixed(2)}%)
                  </div>
                  <div className="text-xl font-bold text-gray-800">
                    {formatCurrency(profitLoss?.actual)} ({(Number(profitLossPercentage?.actual) || 0).toFixed(2)}%)
                  </div>
                  <div className="text-xl font-bold text-gray-800">
                    {formatCurrency((Number(profitLoss?.actual) || 0) - computedBudgetPL)}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Invoices */}
        <div className="bg-[#F8F8F8] rounded-lg shadow-md">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-semibold text-gray-800">List Of Invoices</span>
            </div>
            <div className="overflow-auto max-h-[400px]">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Number</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Amount</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    invoices.length > 0 ? (
                      invoices.map((invoice) => {
                        let data = JSON.parse(invoice.data);
                        data = normalizeKeys(data);
                        let date, dueDate;
                        if (data.dateString) date = new Date(data.dateString);
                        else if (data.date) date = new Date(data.date);
                        if (data.dueDateString) dueDate = new Date(data.dueDateString);
                        else if (data.dueDate) dueDate = new Date(data.dueDate);

                        return (
                          <tr key={data.id} className="border-b">
                            <td className="px-4 py-2 text-sm text-gray-700">{data.type == "ACCRECCREDIT" ? data.creditNoteNumber : data.invoiceNumber}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{formatDateToDDMMYYYY(date)}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{invoice?.total}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data.status}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="text-center py-4" colSpan={5}>
                          No invoices available
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td className="text-center py-4" colSpan={5}>
                        <FontAwesomeIcon icon={faSpinner} size="2xl" style={{ color: "#55A14A" }} spin />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Bills */}
        <div className="bg-[#F8F8F8] rounded-lg shadow-md">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-semibold text-gray-800">List Of Bills</span>
            </div>
            <div className="overflow-auto max-h-[400px] min-h[400px]">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Number</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Supplier</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Amount</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    bills.length > 0 ? (
                      bills.map((bill) => {
                        let data = JSON.parse(bill.data);
                        data = normalizeKeys(data);
                        let date, dueDate;

                        if (data.dateString) date = new Date(data.dateString);
                        else if (data.date) date = new Date(data.date);
                        if (data.dueDateString) dueDate = new Date(data.dueDateString);
                        else if (data.dueDate) dueDate = new Date(data.dueDate);

                        return (
                          <tr key={data.id} className="border-b">
                            <td className="px-4 py-2 text-sm text-gray-700">{data.type == "ACCPAYCREDIT" ? data.creditNoteNumber : data.invoiceNumber}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data?.contact?.name}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{formatDateToDDMMYYYY(date)}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{bill?.total}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data.status}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="text-center py-4" colSpan={5}>
                          No bills available
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td className="text-center py-4" colSpan={5}>
                        <FontAwesomeIcon icon={faSpinner} size="2xl" style={{ color: "#55A14A" }} spin />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Expenses */}
        <div className="bg-[#F8F8F8] rounded-lg shadow-md">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-semibold text-gray-800">Project Expenses</span>
            </div>
            <div className="overflow-auto max-h-[400px] min-h[400px]">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">User</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Category</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    projectExpenseData?.length > 0 ? (
                      projectExpenseData?.map((data) => {
                        let date;
                        if (data.expense_date) date = new Date(data.expense_date);
                        return (
                          <tr key={data.id} className="border-b">
                            <td className="px-4 py-2 text-sm text-gray-700">{formatDateToDDMMYYYY(date)}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data?.customer_name}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data?.category_name}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(data?.line_amount)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="text-center py-4" colSpan={5}>
                          No expenses available
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td className="text-center py-4" colSpan={5}>
                        <FontAwesomeIcon icon={faSpinner} size="2xl" style={{ color: "#55A14A" }} spin />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Mileage */}
        <div className="bg-[#F8F8F8] rounded-lg shadow-md">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-semibold text-gray-800">Project Mileage</span>
            </div>
            <div className="overflow-auto max-h-[400px] min-h[400px]">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">User</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Reason</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Miles</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    projectMileageData?.length > 0 ? (
                      projectMileageData?.map((data) => {
                        let date;
                        if (data.date) date = new Date(data.date);
                        return (
                          <tr key={data.id} className="border-b">
                            <td className="px-4 py-2 text-sm text-gray-700">{formatDateToDDMMYYYY(date)}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data?.customer_name}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data?.reason}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data?.miles}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(data?.cost)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="text-center py-4" colSpan={5}>
                          No mileage available
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td className="text-center py-4" colSpan={5}>
                        <FontAwesomeIcon icon={faSpinner} size="2xl" style={{ color: "#55A14A" }} spin />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Time Entry */}
        <div className="bg-[#F8F8F8] rounded-lg shadow-md">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-semibold text-gray-800">Project Time Entry</span>
            </div>
            <div className="overflow-auto max-h-[400px] min-h[400px]">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="border-b">
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Date</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">User</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Reason</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Hours</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    projectTimeEntryData?.length > 0 ? (
                      projectTimeEntryData?.map((data) => {
                        let date;
                        if (data.date) date = new Date(data.date);
                        return (
                          <tr key={data.id} className="border-b">
                            <td className="px-4 py-2 text-sm text-gray-700">{formatDateToDDMMYYYY(date)}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data?.customer_name}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data?.reason}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{data?.hours}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(data?.cost)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="text-center py-4" colSpan={5}>
                          No time entry available
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td className="text-center py-4" colSpan={5}>
                        <FontAwesomeIcon icon={faSpinner} size="2xl" style={{ color: "#55A14A" }} spin />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

export default Financials;
